import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  Users,
  Landmark,
  Pill,
  Server,
  Building,
  Factory,
  ArrowRight,
} from 'lucide-react';
import ScrollReveal from '@/components/ScrollReveal';

gsap.registerPlugin(ScrollTrigger);

const industries = [
  {
    icon: Users,
    title: 'Recruitment',
    description: 'AI workflows for modern talent acquisition',
    image: '/images/industries/recruitment.jpg',
    href: '/industries?section=recruitment',
  },
  {
    icon: Landmark,
    title: 'FinTech',
    description: 'Compliance and intelligence for financial services',
    image: '/images/industries/fintech.jpg',
    href: '/industries?section=fintech',
  },
  {
    icon: Pill,
    title: 'Pharma',
    description: 'Knowledge and compliance AI for pharmaceutical operations',
    image: '/images/industries/pharma.jpg',
    href: '/industries?section=pharma',
  },
  {
    icon: Server,
    title: 'IT Platform Engineering & DX',
    description: 'Developer experience tools for Kubernetes, ARM64 migration, and cloud-native infrastructure',
    image: '/images/industries/telecom.jpg',
    href: '/industries?section=platform-engineering',
  },
  {
    icon: Building,
    title: 'Public Sector',
    description: 'Secure AI for public sector operations',
    image: '/images/government.jpg',
    href: '/industries?section=government',
  },
  {
    icon: Factory,
    title: 'Manufacturing',
    description: 'Cognitive Industrial Edge for Industry 4.0',
    image: '/images/industries/manufacturing.jpg',
    href: '/industries?section=manufacturing',
  },
];

export default function Industries() {
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const cards = cardsRef.current?.querySelectorAll('.industry-card');
      if (cards) {
        gsap.fromTo(
          cards,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.1,
            ease: 'expo.out',
            scrollTrigger: {
              trigger: cardsRef.current,
              start: 'top 80%',
            },
          }
        );
      }
    }, cardsRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="section-padding bg-slate-50">
      <div className="container-custom">
        {/* Section Header */}
        <ScrollReveal className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Who We Work With
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Deep expertise across regulated and mission-critical sectors
          </p>
        </ScrollReveal>

        {/* Industries Grid */}
        <div
          ref={cardsRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {industries.map((industry, index) => (
            <Link
              key={index}
              to={industry.href}
              className="industry-card group relative bg-white rounded-xl overflow-hidden border border-slate-200 shadow-card transition-all duration-500 hover:-translate-y-2 hover:shadow-card-hover"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={industry.image}
                  alt={industry.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-transparent" />
              </div>

              {/* Content */}
              <div className="p-6 relative">
                {/* Icon */}
                <div className="absolute -top-8 right-6 w-14 h-14 bg-primary rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <industry.icon className="w-7 h-7 text-white" />
                </div>

                <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-primary transition-colors">
                  {industry.title}
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  {industry.description}
                </p>
                <span className="inline-flex items-center gap-2 text-primary text-sm font-medium group-hover:gap-3 transition-all">
                  Explore solutions
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
